// Restore 2FA step (docs/03 §3a): reached only when restoring an account that
// has a password set — the server answered `password_required` to the key
// login. The typed password proves the second factor; the shell derives the
// authKey from it and never stores the password itself.

import { useState, type FormEvent, type ReactNode } from 'react';
import { useT } from '../../i18n/index.js';

export function RestorePassword({
  onSubmit,
  onBack,
  error,
  busy,
}: {
  onSubmit: (password: string) => void;
  /** Abandon this restore (e.g. wrong account) and return to the start. */
  onBack: () => void;
  /** Set when a previous attempt was wrong (server re-answered password_required). */
  error?: string;
  /** True while the login attempt is in flight. */
  busy?: boolean;
}): ReactNode {
  const t = useT();
  const [password, setPassword] = useState('');

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    if (password.length === 0) return;
    onSubmit(password);
  }

  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-6 p-6">
      <h1 className="text-2xl font-semibold text-ink">{t('restorePassword.title')}</h1>
      <p className="text-sm text-muted">{t('restorePassword.help')}</p>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <label className="flex flex-col gap-1 text-sm text-ink">
          {t('restorePassword.label')}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            className="min-h-touch rounded-xl border border-edge bg-surface px-3 py-2 text-ink"
          />
        </label>
        {error !== undefined && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={busy}
            className="min-h-touch flex-1 rounded-xl border border-edge bg-surface px-4 font-medium text-ink disabled:opacity-40"
          >
            {t('common.back')}
          </button>
          <button
            type="submit"
            disabled={busy || password.length === 0}
            className="min-h-touch flex-1 rounded-xl bg-accent px-4 font-medium text-ink disabled:opacity-40 active:bg-accent-press"
          >
            {busy ? t('restorePassword.signingIn') : t('common.continue')}
          </button>
        </div>
      </form>
    </main>
  );
}
